import React, { useEffect, useMemo, useRef, useState } from "react"
import { getMessage } from "../../msgs/msgs"
import writeXlsxFile from "write-excel-file"
import { Button } from "@radix-ui/themes"
import { DataTable, type DataTableColumn } from "../DataTable/DataTable"
import "./ObjectTableViewer.module.css"

type StockRow = {
  sku: string
  amount: string
  code: string
  message: string
}

const columns: DataTableColumn<StockRow>[] = [
  {
    key: "sku",
    header: "SKU",
    cellStyle: () => ({
      fontFamily: '"Courier New", monospace',
      fontSize: 14,
    }),
  },
  {
    key: "amount",
    header: "Amount",
    cellStyle: () => ({ textAlign: "center", fontWeight: 500 }),
  },
  {
    key: "code",
    header: "Код",
    cellStyle: () => ({ color: "#dc2626", fontWeight: 500 }),
  },
  {
    key: "message",
    header: "Сообщение",
    cellStyle: () => ({ color: "#6b7280" }),
  },
]

const StocksTable = ({ data }: { data: any }) => {
  const errors = data.errors || data
  const errorTitle = data.type && getMessage(data.type)
  const [copyStatus, setCopyStatus] = useState<string>("")
  const copyStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  useEffect(() => {
    return () => {
      if (copyStatusTimeoutRef.current) {
        clearTimeout(copyStatusTimeoutRef.current)
      }
    }
  }, [])

  const scheduleCopyStatusReset = () => {
    if (copyStatusTimeoutRef.current) {
      clearTimeout(copyStatusTimeoutRef.current)
    }
    copyStatusTimeoutRef.current = setTimeout(() => {
      setCopyStatus("")
      copyStatusTimeoutRef.current = null
    }, 2000)
  }

  const allStocks = useMemo(() => {
    const values: any[] = errors.flatMap((error: any) => error.data)
    return values.map((stock) => {
      const error = errors.find((err: any) =>
        err.data.some((item: any) => item.sku === stock.sku),
      )
      return {
        sku: stock.sku,
        amount: stock.amount.toString(),
        code: error?.code || "N/A",
        message: error?.message || "N/A",
      }
    })
  }, [errors])

  const copyTableToClipboard = async () => {
    try {
      const headers = ["SKU", "Amount", "Код", "Сообщение"]
      const rows: any[] = allStocks.map((stock: any) => {
        return [
          stock.sku.toString(),
          stock.amount.toString(),
          stock?.code || "N/A",
          stock?.message || "N/A",
        ]
      })

      // Формируем CSV-like строку
      const csvContent = [
        headers.join("\t"),
        ...rows.map((row) => row.join("\t")),
      ].join("\n")

      // Копируем в буфер обмена
      await navigator.clipboard.writeText(csvContent)

      setCopyStatus("Таблица скопирована!")
      scheduleCopyStatusReset()
    } catch (err) {
      console.error("Ошибка копирования:", err)
      setCopyStatus("Ошибка копирования")
      scheduleCopyStatusReset()
    }
  }

  const exportToExcel = () => {
    writeXlsxFile(allStocks, {
      schema: [
        { column: "SKU", value: (el) => el.sku },
        { column: "Amount", value: (el) => el.amount },
        { column: "Code", value: (el) => el.code },
        { column: "Message", value: (el) => el.message },
      ],
      fileName: "ошибки_при_обновлении.xlsx",
    })
  }

  if (!allStocks.length) {
    return null
  }
  return (
    <div className="stocks-table-container">
      <div className="table-header">
        {errorTitle ? <h2>{errorTitle}</h2> : null}
        <div className="flex gap-10 flex-align-center">
          <Button onClick={exportToExcel}>Выгрузить в excel</Button>
          {copyStatus ? (
            <span className="copy-status">{copyStatus}</span>
          ) : (
            <Button
              onClick={copyTableToClipboard}
              className="copy-btn"
              title="Скопировать как таблицу (для Excel)"
            >
              Копировать таблицу
            </Button>
          )}
        </div>
      </div>
      <div className="table-wrapper">
        <DataTable
          columns={columns}
          data={allStocks}
          rowKey={(stock, i) => `${stock.sku}-${i}`}
          rowClassName={(stock) => (stock.amount === "0" ? "zero-stock" : "")}
          variant="surface"
          size="2"
        />
      </div>
    </div>
  )
}

export { StocksTable }
